// keywordAutocomplete/caretWord.js — the read side of a contenteditable-capable keyword autocomplete.
//
// Legacy dynAutocomplete (basic.js) reads the "current keyword" from a <textarea> via `$t.val()` +
// `$t[0].selectionStart`, then extractTerm() slices the comma-delimited word around the caret. contenteditable
// has neither .val() nor selectionStart — so here we compute the same thing via the Selection/Range API:
// the caret's CHARACTER offset (counting text across all child nodes, incl. hidden-keyword <mark> spans),
// then apply the identical comma-boundary logic. Pure `extractTermLike` is unit-tested; getWordAtCaret is the
// DOM wrapper.

// The comma-delimited keyword the caret sits inside (between the comma before and the comma after), trimmed.
// Same boundaries as legacy basic.js extractTerm, but WITHOUT its two edge quirks: (1) editing mid-way through
// the LAST keyword dropped the final char (substring(0,-1)); (2) with the caret right before a comma the next
// keyword bled in. Corrected here (this is new code) — a clean comma split.
export function extractTermLike(text, pos) {
    const start = text.lastIndexOf(',', pos - 1) + 1;   // char after the comma before the caret (0 if none)
    const after = text.indexOf(',', pos);
    const end = after === -1 ? text.length : after;     // comma after the caret (or end of text)
    return text.substring(start, end).trim();
}

// The caret's character offset from the start of `el`, or null if the caret is not inside `el`.
// A cloned range from el-start to the caret; its stringified length = chars before the caret (== selectionStart).
export function caretCharOffset(el) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) { return null; }
    const range = sel.getRangeAt(0);
    if (!el.contains(range.endContainer)) { return null; }
    const pre = range.cloneRange();
    pre.selectNodeContents(el);
    pre.setEnd(range.endContainer, range.endOffset);
    return pre.toString().length;
}

// The comma-delimited keyword the caret currently sits in (trimmed). '' if the caret isn't in `el`.
export function getWordAtCaret(el) {
    const offset = caretCharOffset(el);
    if (offset === null) { return ''; }
    return extractTermLike(el.textContent, offset);
}

// ── write side ──

// Pure: replace the comma-word around `offset` in `text` with `replacement`, keeping the leading space after a
// comma (", keyword") like the legacy join(', '). addSeparator appends ", " so the user can keep typing the next
// keyword (the .keywords-field behavior). Returns the new text and where the caret should go (right after the
// inserted term). The contenteditable equivalent of the textarea value-splice + selectionStart reset.
export function spliceTermAtCaret(text, offset, replacement, { addSeparator = false } = {}) {
    const start = text.lastIndexOf(',', offset - 1) + 1;
    const afterComma = text.indexOf(',', offset);
    const end = afterComma === -1 ? text.length : afterComma;
    const lead = /^\s*/.exec(text.slice(start, end))[0];
    const insert = lead + replacement + (addSeparator ? ", " : "");
    return { text: text.slice(0, start) + insert + text.slice(end), caret: start + insert.length };
}

// Place the caret at character offset `offset` within `el` (walking text nodes) — the inverse of caretCharOffset.
// Offsets past the end clamp to the end of the content.
export function setCaretCharOffset(el, offset) {
    const range = document.createRange();
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let remaining = offset;
    let last = null;
    let node;
    while ((node = walker.nextNode())) {
        last = node;
        if (node.nodeValue.length >= remaining) {
            range.setStart(node, remaining);
            range.collapse(true);
            applyRange(range);
            return;
        }
        remaining -= node.nodeValue.length;
    }
    if (last) { range.setStart(last, last.nodeValue.length); }
    else { range.selectNodeContents(el); }
    range.collapse(true);
    applyRange(range);
}

function applyRange(range) {
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
}
